import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { message } from 'antd';
import API_URL from '../../config/api';
import { clearSession } from '../../data/action';
import useSessionBootstrap from '../../data/useSessionBootstrap';
import './header.css';

// Where each role's dashboard lives, for the header's shortcut link.
const DASHBOARD_FOR_ROLE = {
    admin: '/dashboard/mainBody',
    agent: '/dashboardAgent/MainBodyAgent',
};

const Header = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Ask the API who is signed in. The header renders on public pages too,
    // where the route guard never runs, so it cannot rely on that.
    useSessionBootstrap();

    // Previously this read cookies named access_token / refresh_token, which
    // nothing in the app has ever set - login issues httpOnly accessToken and
    // refreshToken, which JavaScript cannot read at all. So the header tracked
    // a value it invented itself and always showed "Login".
    const isLoggedIn = useSelector((state) => state.isAuthenticated === true);
    const role = useSelector((state) => state.role);
    // eslint-disable-next-line
    const [scrollPosition, setScrollPosition] = useState(0);
    const headerRef = React.createRef();
    const [showMenu, setShowMenu] = useState(false); // State for menu visibility

    const handleScroll = () => {
        setScrollPosition(window.scrollY);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // handleLogin used to write the literal strings 'your_access_token' and
    // 'your_refresh_token' into cookies - a placeholder that made the header
    // look signed in without any session existing. Signing in is the login
    // page's job, so the button simply links there now.

    const handleLogout = async () => {
        try {
            await axios.post(`${API_URL}/users/logout`);
        } catch (error) {
            // The session is being discarded either way; a failed call should
            // not strand the user in a logged-in-looking header.
            console.error('Logout request failed:', error.message);
        }

        // The auth cookies are httpOnly, so only the server can clear them.
        dispatch(clearSession());
        localStorage.removeItem('role');
        localStorage.removeItem('email');
        message.success('Logged out');
        navigate('/');
    };

    const shouldHideHeader =
        location.pathname.startsWith('/dashboard') ||
        location.pathname.startsWith('/signup') ||
        location.pathname.startsWith('/reset-password') ||
        location.pathname.startsWith('/dashboardAgent') ||
        location.pathname.startsWith('/login');

    if (shouldHideHeader) {
        return null;
    }

    return (
        <nav
            id="header"
            className="fixed z-30 top-0 mt-3 text-black bg-white rounded-full w-full"
            style={{ marginLeft: "1rem", marginRight: "1rem" }}
            ref={headerRef}
        >
            <div className="container mx-auto flex flex-row items-center justify-between py-3">
                <div className="pl-4 flex items-center">
                    <Link
                        to="/"
                        className="toggleColour text-black no-underline hover:no-underline font-bold text-2xl lg:text-4xl"
                    >
                        {/* Served from public/ with a stable filename so the
                            preload hint in index.html can reference it. An
                            imported asset would carry a build-time content
                            hash that static HTML cannot know. fetchpriority
                            reinforces the hint for browsers that ignore
                            preload ordering. */}
                        <img
                            src={`${process.env.PUBLIC_URL}/logo-house.png`}
                            alt="DreamHouse"
                            className='rounded-full'
                            fetchpriority="high"
                            width="69"
                            height="69"
                            style={{ height: '4.3rem' }}
                        />
                    </Link>
                    <span className="text-2xl ml-2 font-bold">DreamHouse</span>
                </div>

                {/* Menu for larger screens */}
                <ul className="lg:flex justify-end items-center flex-grow lg:mr-4">
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/"
                            className="inline-block py-2 px-3 text-2xl text-black hover:text-yellow-500  no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Home
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/buy"
                            className="inline-block py-2 px-3  text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Buy
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/sell"
                            className="inline-block py-2 px-3 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Sell
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/agent"
                            className="inline-block py-2 px-3 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Agent
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/about"
                            className="inline-block py-2 px-3 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            About
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/contact"
                            className="inline-block py-2 px-4 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Contact
                        </Link>
                    </li>
                </ul>

                {/* Menu toggle button for smaller screens */}
                <div className="flex lg:hidden">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        aria-controls="mobile-menu"
                        aria-expanded="false"
                    >
                        <span className="sr-only">Open main menu</span>
                        {showMenu ? (
                            <svg
                                className="block h-6 w-6"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="block h-6 w-6"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            </svg>
                        )}
                    </button>
                </div>

                <div className="flex items-center gap-3">
                    {/* A staff member gets a shortcut back to their dashboard. */}
                    {isLoggedIn && DASHBOARD_FOR_ROLE[role] && (
                        <Link
                            to={DASHBOARD_FOR_ROLE[role]}
                            className="hidden lg:inline-block no-underline text-white font-bold underline-offset-4 hover:underline"
                        >
                            Dashboard
                        </Link>
                    )}

                    {/* The LogOut button used to sit inside <Link to="/login">, so
                        clicking it navigated to the login page - it never logged
                        anyone out. It is a plain button now. */}
                    {isLoggedIn ? (
                        <button
                            type="button"
                            className=" bg-[#d63d32] border-2 border-white  no-underline text-black font-bold rounded-full  lg:mt-0 py-4 px-8 transform transition duration-300 ease-in-out hover:scale-110 hover:text-white hover:font-bold"
                            onClick={handleLogout}
                        >
                            LogOut
                        </button>
                    ) : (
                        <Link to="/login">
                            <button
                                type="button"
                                className=" bg-darkGreen border-2 border-white  no-underline text-black font-bold rounded-full lg:mt-0 py-4 px-8 transform transition duration-300 ease-in-out hover:scale-110 hover:text-white hover:font-bold"
                            >
                                LogIn
                            </button>
                        </Link>
                    )}
                </div>
            </div>


            {/* Mobile menu */}
            <div className={`lg:hidden ${showMenu ? 'block' : 'hidden'}`}>
                <div className="px-2 pt-2 pb-3 space-y-1">
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/"
                            className="inline-block py-2 px-3 text-2xl text-black hover:text-yellow-500  no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Home
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/buy"
                            className="inline-block py-2 px-3  text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Buy
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/sell"
                            className="inline-block py-2 px-3 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Sell
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/agent"
                            className="inline-block py-2 px-3 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Agent
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/about"
                            className="inline-block py-2 px-3 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            About
                        </Link>
                    </li>
                    <li className="my-3 lg:my-0">
                        <Link
                            to="/contact"
                            className="inline-block py-2 px-4 text-black text-2xl no-underline hover:text-2xl hover:bg-gray-100 rounded-full hover:p-2"
                        >
                            Contact
                        </Link>
                    </li>
                </div>
            </div>

        </nav>
    );
};

export default Header;
